/**
 * Widget Configuration Hook
 */

import { useContext } from 'react';
import { WidgetContext } from '../WidgetProvider';
import type { ResolvedWidgetConfig } from '../types';

/**
 * Hook to access widget configuration from context
 * @throws Error if used outside of WidgetProvider
 */
export function useWidgetConfig(): ResolvedWidgetConfig {
  const context = useContext(WidgetContext);

  if (!context) {
    throw new Error(
      'useWidgetConfig must be used within a WidgetProvider. ' +
      'Make sure your component is wrapped in the Widget component.'
    );
  }

  return context.config;
}

/**
 * Hook to optionally access widget configuration from context
 * Returns null if used outside of WidgetProvider (useful for shared components)
 */
export function useOptionalWidgetConfig(): ResolvedWidgetConfig | null {
  const context = useContext(WidgetContext);
  return context?.config ?? null;
}

/**
 * Hook to access the update config function
 */
export function useUpdateWidgetConfig(): (config: Partial<ResolvedWidgetConfig>) => void {
  const context = useContext(WidgetContext);

  if (!context) {
    throw new Error(
      'useUpdateWidgetConfig must be used within a WidgetProvider.'
    );
  }

  return context.updateConfig;
}
